import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import CustomSelect from 'components/common/CustomSelect';
import CustomInput from 'components/common/CustomInput';
import CustomButton from 'components/common/CustomButton';
import microsoftSVG from 'assets/images/svg/microsoft.svg';
import FlowardLogo from 'assets/images/svg/FlowardLogo';
import ListIcon from 'assets/images/svg/ListIcon';
import ClockIcon from 'assets/images/svg/ClockIcon';
import UserIcon from 'assets/images/svg/UserIcon';
import PhoneIcon from 'assets/images/svg/PhoneIcon';
import LockIcon from 'assets/images/svg/LockIcon';
import EyeIcon from 'assets/images/svg/EyeIcon';
import EyeOffIcon from 'assets/images/svg/EyeOffIcon';
import AuthService from 'services/AuthService';
// actions
import { setIsLoggedInTrue } from 'store/app/AppActions';
import { addToLocalStorage } from 'helpers/LocalStorage';
import { toast } from 'react-toastify';

// What the florist gets once signed in - shown on the brand panel.
const FEATURES = [
  {
    Icon: ListIcon,
    title: 'Ranked order list',
    text: 'Work through orders in the order they need to leave.',
  },
  {
    Icon: ClockIcon,
    title: 'Live cut-off timers',
    text: 'See how long is left before every order is due.',
  },
  {
    Icon: UserIcon,
    title: 'Assign to Me',
    text: 'Pick up unassigned or dropped orders in one tap.',
  },
];

const LoginPage = () => {
  const [phone, setPhone] = useState(''),
    [password, setPassword] = useState(''),
    [showPassword, setShowPassword] = useState(false),
    [countryId, setCountryId] = useState(-1),
    [disabled, setDisabled] = useState(false),
    [countriesList, setCountriesList] = useState([]),
    navigate = useNavigate(),
    dispatch = useDispatch();

  const handleLogin = async () => {
    try {
      setDisabled(true);
      const { data } = await AuthService.logInFlorist({ phone, password, countryId: +countryId });
      if (!data.hasError) {
        validateSession(data.data);
      }
      setDisabled(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong!');
      setDisabled(false);
    }
  };

  const validateSession = async (sessionId) => {
    try {
      let sessionData = await AuthService.validateSession(sessionId);
      const operationCenter = sessionData.data?.operationCenter;
      addToLocalStorage('token', sessionData.data?.token);
      // localStorage stringifies, so writing a missing id stores the literal "null".
      if (operationCenter?.id != null) {
        addToLocalStorage('opsId', operationCenter.id);
        addToLocalStorage('opsName', operationCenter.name ?? '');
      } else {
        localStorage.removeItem('opsId');
        localStorage.removeItem('opsName');
        toast.error('Your account has no operation centre linked to it.');
      }
      addToLocalStorage('floristId', sessionData.data?.user?.id);
      dispatch(setIsLoggedInTrue());
      navigate('/');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong!');
    }
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${process.env.REACT_APP_MICROSOFT_LOGIN}`;
  };

  const getCountryList = async () => {
    try {
      const res = await AuthService.getCountryCodes();
      res.data?.forEach((c) => (c.Code = '+' + c.Code));
      setCountriesList(res.data);
    } catch (err) {
      // error in getting country list
    }
  };

  const countryTemplate = (rowData) => (
    <div id={rowData.Id}>
      <span id={rowData.Id}>
        {rowData.Code}&nbsp;{rowData.Name}
      </span>
    </div>
  );

  // const handleOpen = (e) => {
  //   e.popup.element.querySelector('[aria-owns="countryLogin_options"]').name = 'search';
  // };

  useEffect(() => {
    getCountryList();
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const sessionId = urlParams.get('sessionId');
    if (sessionId) {
      validateSession(sessionId);
    }
    const isFailed = urlParams.get('status');
    if (isFailed === 'failed') {
      toast.error('Microsoft login has failed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="login-page">
      <aside className="login-brand">
        <FlowardLogo cssClass="login-brand-logo" />

        <div className="login-brand-body">
          <span className="login-brand-eyebrow">
            <span className="login-brand-dot" />
            Florist workspace
          </span>
          <h1 className="login-brand-title">Prepare every order, right on time.</h1>
          <p className="login-brand-text">
            Ranked orders, live cut-off timers and one-tap assignment, all in one place.
          </p>
          <ul className="login-features">
            {FEATURES.map(({ Icon, title, text }) => (
              <li key={title} className="login-feature">
                <span className="login-feature-icon">
                  <Icon />
                </span>
                <span className="login-feature-text">
                  <b>{title}</b>
                  {text}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="login-brand-footer">&copy; {new Date().getFullYear()} Floward</p>
      </aside>

      <main className="login-panel">
        <div className="login-card">
          <h2 className="login-title">Welcome back</h2>
          <p className="login-subtitle">Sign in to your florist account to continue.</p>

          <form className="login-form" onSubmit={(e) => e.preventDefault()} autoComplete="off">
            <div className="login-field">
              <label className="login-label" htmlFor="phoneLogin">
                Phone number
              </label>
              <div className="login-phone-group">
                <div className="login-country">
                  <CustomSelect
                    id="countryLogin"
                    placeholder="Country"
                    listData={countriesList}
                    textPropName="Code"
                    valuePropName="Id"
                    handleChange={(e) => setCountryId(+e.itemData.Id)}
                    selectedValue={countryId}
                    template={countryTemplate}
                    // delayUpdate
                    // onOpen={handleOpen}
                    allowFiltering={false}
                    popupWidth="260px"
                  />
                </div>
                <div className="login-input">
                  <PhoneIcon cssClass="login-input-icon" />
                  <CustomInput
                    id="phoneLogin"
                    value={phone}
                    handleChange={(e) => setPhone(e.target.value)}
                    type="number"
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </div>

            <div className="login-field">
              <label className="login-label" htmlFor="passwordLogin">
                Password
              </label>
              <div className="login-input has-toggle">
                <LockIcon cssClass="login-input-icon" />
                <CustomInput
                  id="passwordLogin"
                  value={password}
                  handleChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                />
                {/* type="button" keeps Enter submitting through the sign in button. */}
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((shown) => !shown)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <CustomButton
              id="regularLogin"
              name={disabled ? 'Signing in…' : 'Sign in'}
              handleClick={handleLogin}
              disabled={!phone || !password || countryId === -1 || disabled}
              type={'login'}
            />

            <div className="login-divider">
              <span>or</span>
            </div>

            <CustomButton
              id="msLogin"
              name="Sign in with Microsoft"
              handleClick={handleMicrosoftLogin}
              type={'login-microsoft'}
              icon={microsoftSVG}
              iconCss="ms-icon"
            />
          </form>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
